import EditIcon from '@mui/icons-material/Edit';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { useCallback, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { applicationSlice } from '../../../redux/ApplicationSlice';
import { useAppDispatch, useAppSelector } from '../../../redux/ReduxHooks';
import { ApplicationActions } from '../../../utils/constants';

interface ProjectTitleBarProps {
  measureRef?: RefObject<HTMLDivElement>;
}

const ProjectTitleBar = ({ measureRef }: ProjectTitleBarProps) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const project = useAppSelector((state) => state.project.loadedProject);
  const showReadOnlyLabel = project?.accessLevel === 'viewer';

  const handleOpenEditProjectNameDialog = useCallback(() => {
    if (!project) return;

    const dialog = {
      ...ApplicationActions.Dialogs.PROJECT_NAME,
      options: {
        ...ApplicationActions.Dialogs.PROJECT_NAME.options,
        isEditing: true,
        projectId: project.id,
        title: 'projectNameDialog.edit.title',
      },
    };

    dispatch(applicationSlice.actions.onOpenDialog(dialog));
  }, [dispatch, project]);

  if (!project) return null;

  const readOnlyLabel = t('primaryBar.label.read-only', 'READ-ONLY');

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        minWidth: 0,
        maxWidth: '100%',
        width: '100%',
        overflow: 'hidden',
        justifyContent: 'center',
        position: 'relative',
      }}
    >
      <Box
        ref={measureRef}
        aria-hidden
        sx={{
          position: 'absolute',
          visibility: 'hidden',
          pointerEvents: 'none',
          whiteSpace: 'nowrap',
          height: 0,
          overflow: 'hidden',
          display: 'inline-flex',
          alignItems: 'center',
        }}
      >
        <Box component="span" sx={{ typography: 'projectTitle' }}>
          {project.name}
        </Box>
        {showReadOnlyLabel ? (
          <Typography component="span" variant="projectTitle" color="info.main" sx={{ ml: 1 }}>
            {readOnlyLabel}
          </Typography>
        ) : null}
      </Box>
      <Box
        data-testid="project-title-group"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          minWidth: 0,
          maxWidth: '100%',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ minWidth: 0, flex: '1 1 0', overflow: 'hidden' }}>
          <Tooltip title={project.name ?? ''} enterDelay={500}>
            <Typography
              variant="projectTitle"
              color="text.primary"
              noWrap
              component="span"
              data-testid="project-title"
              sx={{
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {project.name}
            </Typography>
          </Tooltip>
        </Box>
        {showReadOnlyLabel ? (
          <Typography component="span" variant="projectTitle" color="info.main" data-testid="project-read-only-label" sx={{ ml: 1, flexShrink: 0 }}>
            {readOnlyLabel}
          </Typography>
        ) : null}
        {!project.readOnly && (
          <IconButton
            size="small"
            onClick={handleOpenEditProjectNameDialog}
            aria-label={t('aria-label.edit-project-name', 'edit project name')}
            sx={{ ml: 0.5, flexShrink: 0 }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
    </Box>
  );
};

export default ProjectTitleBar;
